/**
 * Frontend telemetry for Feeling Froggy.
 *
 * Sends events to /api/store/telemetry/batch/ for persistence in TelemetryEvent table.
 * Works for both anonymous and authenticated users.
 *
 * Usage:
 *   import { trackEvent, trackCartEvent, trackCheckoutEvent } from '../utils/telemetry';
 *
 *   // Generic event
 *   trackEvent('page_view', { page_title: 'Shop', url_path: '/shop' });
 *
 *   // Cart events
 *   trackCartEvent('cart_item_added', cartId, { variation_id: '...', quantity: 2 });
 *
 *   // Checkout events
 *   trackCheckoutEvent('checkout_started', orderId, { item_count: 3 });
 */

import { getOrCreateClientId } from './clientId';

// Batch events to reduce network calls
let eventQueue = [];
let flushTimer = null;
const FLUSH_INTERVAL = 5000; // 5 seconds
const MAX_BATCH_SIZE = 50;

/**
 * Get the API base URL for telemetry endpoint.
 */
const getTelemetryEndpoint = () => {
  // Use the same base URL as the rest of the app
  // REACT_APP_API_URL already includes /api, so we just append store/telemetry/batch/
  const baseUrl = process.env.REACT_APP_API_URL || '';
  return `${baseUrl}/store/telemetry/batch/`;
};

/**
 * Flush queued events to the backend.
 * Best-effort: never throws, always resolves.
 */
const flushEvents = async () => {
  if (eventQueue.length === 0) return;

  const eventsToSend = eventQueue.slice(0, MAX_BATCH_SIZE);
  eventQueue = eventQueue.slice(MAX_BATCH_SIZE);

  try {
    // Note: client_uuid is included in each event's body (see queueEvent),
    // so we don't need a custom header. Using only Content-Type avoids
    // triggering a CORS preflight request.
    const response = await fetch(getTelemetryEndpoint(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ events: eventsToSend }),
      // Don't wait for response - fire and forget
      keepalive: true,
    });

    if (response.ok) {
      const data = await response.json();
      console.log(`[TELEMETRY] Flushed ${data.created} events`);
    } else {
      console.warn('[TELEMETRY] Batch endpoint returned non-OK status:', response.status);
    }
  } catch (error) {
    // Best-effort: log but don't throw
    console.warn('[TELEMETRY] Failed to flush events:', error.message);
  }

  // If there are more events queued, schedule another flush
  if (eventQueue.length > 0) {
    scheduleFlush();
  }
};

/**
 * Schedule a flush after FLUSH_INTERVAL ms.
 */
const scheduleFlush = () => {
  if (flushTimer) return; // Already scheduled
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEvents();
  }, FLUSH_INTERVAL);
};

/**
 * Queue an event for batched sending.
 * Best-effort: never throws, always succeeds or silently fails.
 * @param {Object} event - Event data to queue
 */
const queueEvent = (event) => {
  try {
    // Safely get client ID - may fail if localStorage is disabled
    let clientId = null;
    try {
      clientId = getOrCreateClientId();
    } catch (e) {
      console.warn('[TELEMETRY] Failed to get client ID:', e.message);
    }

    const enrichedEvent = {
      ...event,
      client_uuid: clientId,
      timestamp: new Date().toISOString(),
      url_path: window.location.pathname,
      referrer: document.referrer || null,
    };

    eventQueue.push(enrichedEvent);
    console.log('[TELEMETRY] Queued event:', event.event_type, enrichedEvent);

    // Flush immediately if batch is full, otherwise schedule
    if (eventQueue.length >= MAX_BATCH_SIZE) {
      flushEvents();
    } else {
      scheduleFlush();
    }
  } catch (error) {
    // Absolute last-resort catch - telemetry should never break the app
    console.warn('[TELEMETRY] queueEvent failed:', error.message);
  }
};

/**
 * Track a generic event.
 * @param {string} eventType - Event type (e.g., 'page_view', 'search_performed')
 * @param {Object} metadata - Additional event data
 */
export const trackEvent = (eventType, metadata = {}) => {
  queueEvent({
    event_type: eventType,
    metadata,
  });
};

/**
 * Track a cart-related event.
 * @param {string} eventType - Event type (e.g., 'cart_item_added', 'cart_item_removed')
 * @param {string|null} cartId - Cart UUID if available
 * @param {Object} metadata - Additional event data (variation_id, quantity, etc.)
 */
export const trackCartEvent = (eventType, cartId = null, metadata = {}) => {
  queueEvent({
    event_type: eventType,
    cart_id: cartId,
    metadata,
  });
};

/**
 * Track a checkout-related event.
 * @param {string} eventType - Event type (e.g., 'checkout_started', 'checkout_address_submitted')
 * @param {string|null} orderId - Order UUID if available
 * @param {Object} metadata - Additional event data
 */
export const trackCheckoutEvent = (eventType, orderId = null, metadata = {}) => {
  queueEvent({
    event_type: eventType,
    order_id: orderId,
    metadata,
  });
};

/**
 * Track a promo-related event.
 * @param {string} eventType - Event type ('promo_applied', 'promo_removed', 'promo_failed')
 * @param {string} promoCode - The promo code involved
 * @param {Object} metadata - Additional event data (discount_amount, error_code, etc.)
 */
export const trackPromoEvent = (eventType, promoCode, metadata = {}) => {
  queueEvent({
    event_type: eventType,
    metadata: {
      promo_code: promoCode,
      ...metadata,
    },
  });
};

/**
 * Track frontend errors.
 * @param {Error} error - The error object
 * @param {Object} context - Additional context (component, action, etc.)
 */
export const trackError = (error, context = {}) => {
  queueEvent({
    event_type: 'frontend_error',
    metadata: {
      error_message: error?.message || String(error),
      error_stack: error?.stack?.substring(0, 500) || null,
      error_name: error?.name || 'Error',
      ...context,
    },
  });
};

/**
 * Track a page view.
 * @param {string} pageTitle - Page title or route name
 * @param {Object} metadata - Additional data (filters, search params, etc.)
 */
export const trackPageView = (pageTitle, metadata = {}) => {
  trackEvent('page_view', {
    page_title: pageTitle,
    ...metadata,
  });
};

/**
 * Force flush all queued events immediately.
 * Call this on page unload or navigation.
 */
export const flushTelemetry = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  flushEvents();
};

/**
 * Get CSRF token from cookie for fallback fetch requests.
 * @returns {string|null} CSRF token or null if not found
 */
const getCsrfToken = () => {
  try {
    const match = document.cookie.match(/csrftoken=([^;]+)/);
    return match ? match[1] : null;
  } catch (e) {
    return null;
  }
};

/**
 * Flush events when page is about to unload.
 * Uses sendBeacon with Blob (application/json) for reliability.
 * Falls back to fetch with keepalive if sendBeacon fails.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (eventQueue.length > 0) {
      const eventsToSend = eventQueue.slice(0, MAX_BATCH_SIZE);
      const payload = JSON.stringify({ events: eventsToSend });

      try {
        // Use Blob with explicit Content-Type to avoid 415 errors
        // sendBeacon with a plain string sends as text/plain, which DRF rejects
        const blob = new Blob([payload], { type: 'application/json' });
        const success = navigator.sendBeacon(getTelemetryEndpoint(), blob);

        if (success) {
          console.log(`[TELEMETRY] Beacon sent ${eventsToSend.length} events on unload`);
        } else {
          // sendBeacon returned false (queue full or other issue), try fetch fallback
          console.warn('[TELEMETRY] sendBeacon returned false, trying fetch fallback');
          const headers = { 'Content-Type': 'application/json' };
          const csrfToken = getCsrfToken();
          if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
          }
          fetch(getTelemetryEndpoint(), {
            method: 'POST',
            headers,
            body: payload,
            credentials: 'same-origin',
            keepalive: true,
          }).catch(() => {
            // Silent fail - we're unloading anyway
          });
        }
      } catch (error) {
        // sendBeacon threw (rare), try fetch fallback
        console.warn('[TELEMETRY] sendBeacon threw:', error.message);
        try {
          const headers = { 'Content-Type': 'application/json' };
          const csrfToken = getCsrfToken();
          if (csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
          }
          fetch(getTelemetryEndpoint(), {
            method: 'POST',
            headers,
            body: payload,
            credentials: 'same-origin',
            keepalive: true,
          }).catch(() => {
            // Silent fail - we're unloading anyway
          });
        } catch (e) {
          // Absolute last resort - nothing we can do
        }
      }
    }
  });
}

// Export for testing
export const _testing = {
  getEventQueue: () => eventQueue,
  clearQueue: () => { eventQueue = []; },
  setFlushInterval: (ms) => { /* for testing */ },
};

// No-op change to trigger Cloudflare Pages rebuild - 2025-12-09
